import { IResponse } from "../types";
import { v7 as uuidv7 } from "uuid";

export enum FlagbagServerStatus {
  NOT_ASSIGN = "unassigned",
  ASSIGNED = "assigned",
  COLLECTED = "collected",
  COLLECTED_EMPTY = "collected_empty",
  COLLECTED_OTHER = "collected_other",
  DISTRIBUTED = "distributed",
  DISTRIBUTED_OTHER = "distributed_other",
  BANK = "collected_sub_to_bank",
  LOSS = "lost",
  LATE = "late",
}

export { FlagbagServerStatus as StatusCode };

export enum FlagbagFilterStatus {
  ALL = "all",
  NOT_ASSIGN = "unassigned",
  ASSIGNED = "assigned",
  DISTRIBUTED = "distributed",
  DISTRIBUTED_OTHER = "distributed_other",
  COLLECTED = "collected",
  COLLECTED_EMPTY = "collected_empty",
  COLLECTED_OTHER = "collected_other",
  BANK = "collected_sub_to_bank",
  LOSS = "lost",
  LATE = "late",
  RECEIVED_TOTAL = "received_total",
  NOT_RECEIVED_TOTAL = "not_received_total",
  EXTRA = "extra",
  LESS = "less",
  RESERVED = "reserved",
}

export enum StatusAllowDistribute {
  NOT_ASSIGN = FlagbagServerStatus.NOT_ASSIGN,
  ASSIGNED = FlagbagServerStatus.ASSIGNED,
}

export enum StatusAllowCollect {
  DISTRIBUTED = FlagbagServerStatus.DISTRIBUTED,
  DISTRIBUTED_OTHER = FlagbagServerStatus.DISTRIBUTED_OTHER,
  COLLECTED = FlagbagServerStatus.COLLECTED,
  BANK = FlagbagServerStatus.BANK,
  COLLECTED_EMPTY = FlagbagServerStatus.COLLECTED_EMPTY,
  COLLECTED_OTHER = FlagbagServerStatus.COLLECTED_OTHER,
  LOSS = FlagbagServerStatus.LOSS,
  LATE = FlagbagServerStatus.LATE,
}

export enum StatusCollected {
  COLLECTED = FlagbagServerStatus.COLLECTED,
  BANK = FlagbagServerStatus.BANK,
  COLLECTED_EMPTY = FlagbagServerStatus.COLLECTED_EMPTY,
  COLLECTED_OTHER = FlagbagServerStatus.COLLECTED_OTHER,
  LOSS = FlagbagServerStatus.LOSS,
  LATE = FlagbagServerStatus.LATE,
}

export interface FlagBag {
  id: string;
  flagbagNum: string;
  bagType: string; // volunteer or spare
  status: FlagbagServerStatus;
  stationId: string;
  stationName: string;
  finalStationId: string;
  finalStationName: string;
  teamId: string;
  teamName: string;
  volunteerId: string;
  volunteerName: string;
  lastUpdatedBy: string;
  lastUpdatedByName: string;
  lastUpdatedAt: string;
}

export interface FlagBagListResponse extends IResponse {
  data: {
    total: number;
    unassignedTotal: number;
    items: FlagBag[];
  }
}

export interface SingleFlagBagResponse extends IResponse {
  data: FlagBag;
}

export interface FlagBagStatusResponse {
  flagBagId: string;
  comingStatus: FlagbagServerStatus[];
  currentStatus: FlagbagServerStatus;
  flagbagNum: string;
  volunteerName: string;
}

export interface FlagBagStatus {
  flagBagId: string;
  allowedStatusList: FlagbagServerStatus[];
  currentStatus: FlagbagServerStatus;
  flagbagNum: string;
  volunteerName: string;
  bagType?: string;
}

export interface UpdateFlagBagRequest {
  status?: string;
  currentStatus?: string;  // 当前旗袋状态
  finalStationId?: string;
  volunteerId?: string;
}

export interface UpdateFlagBagResponse extends IResponse {
  data: {
    flagbag: FlagBag;
    updatedFlagbags?: {
      add?: FlagBag[];
      update?: FlagBag[];
      delete?: string[];
    };
    detail?: any;
    timestamp?: number;
  };
}

export interface BatchSyncFlagBagRequest {
  logs: {
    id?: string;
    flagbagId: string;
    status: string;
    volunteerId?: string;
    stationId: string;
    createdAt: string;
  }[];
  updates: {
    flagbagId: string;
    status: string;
    volunteerId?: string;
    stationId: string;
    finalStationId?: string;     // The final station ID
    assignedAt?: string;         // The assigned timestamp (ISO string)
    sparedAt?: string;           // The spared timestamp (ISO string)
    distributedAt?: string;      // The distributed timestamp (ISO string)
    distributedOtherAt?: string; // The distributed other timestamp (ISO string)
    collectedAt?: string;        // The collected timestamp (ISO string)
    collectedSubToBankAt?: string; // The collected submit to bank timestamp (ISO string)
    collectedEmptyAt?: string;   // The collected empty timestamp (ISO string)
    collectedOtherAt?: string;   // The collected other timestamp (ISO string)
    lostAt?: string;             // The lost timestamp (ISO string)
    latedAt?: string;            // The lated timestamp (ISO string)
    remarks?: string;            // Remarks for the flagbag
    lastSyncedAt?: string;       // Client last-known server updatedAt (ISO string)
  }[];
}

export interface BatchSyncFlagBagResponse extends IResponse {
  data: {
    successCount: number;
    errorCount: number;
    errors?: Array<{
      flagbagId: string;
      flagbagNum: string;
      currentStatus: string;
      updatedAt: string;
      updatedBy?: string;
      targetStatus: string;
      reason: string;
    }>;
  }
}

export class OfflineData {
  id: string;  // UUIDv7 for unique identification
  stationID: string;
  flagBagID: string;
  flagbagNum?: string;  // 旗袋编号
  updateType: string;
  dataType: string;
  status: string;
  isFreeUpdate: boolean;
  name: string;
  phone: string;
  updatedAt: string;
  lastSyncedAt?: string;
  volunteerId?: string;
  userName?: string;
  syncError?: string;  // 新增：同步错误信息（如果有的话）
  syncFailedAt?: number;  // 新增：同步失败的时间戳
  // Additional fields from backend sync API
  assignedStationAt?: string;  // The assigned station timestamp (ISO string)
  finalStationId?: string;  // The final station ID
  assignedAt?: string;  // The assigned timestamp (ISO string)
  sparedAt?: string;  // The spared timestamp (ISO string)
  distributedAt?: string;  // The distributed timestamp (ISO string)
  distributedOtherAt?: string;  // The distributed other timestamp (ISO string)
  collectedAt?: string;  // The collected timestamp (ISO string)
  collectedSubToBankAt?: string;  // The collected submit to bank timestamp (ISO string)
  collectedEmptyAt?: string;  // The collected empty timestamp (ISO string)
  collectedOtherAt?: string;  // The collected other timestamp (ISO string)
  lostAt?: string;  // The lost timestamp (ISO string)
  latedAt?: string;  // The lated timestamp (ISO string)

  constructor(
    stationID: string,
    flagBagID: string,
    updateType: string,
    dataType: string,
    status: FlagbagServerStatus,
    isFreeUpdate: boolean,
    name: string,
    phone: string,
    updatedAt: string,
    volunteerId?: string,
    userName?: string,
    flagbagNum?: string,
    syncError?: string,
    syncFailedAt?: number,
    assignedStationAt?: string,
    finalStationId?: string,
    assignedAt?: string,
    sparedAt?: string,
    distributedAt?: string,
    distributedOtherAt?: string,
    collectedAt?: string,
    collectedSubToBankAt?: string,
    collectedEmptyAt?: string,
    collectedOtherAt?: string,
    lostAt?: string,
    latedAt?: string
  ) {
    this.stationID = stationID;
    this.flagBagID = flagBagID;
    this.flagbagNum = flagbagNum;
    this.updateType = updateType;
    this.dataType = dataType;
    this.status = status;
    this.isFreeUpdate = isFreeUpdate;
    this.name = name;
    this.phone = phone;
    this.updatedAt = updatedAt;
    this.volunteerId = volunteerId;
    this.userName = userName;
    this.syncError = syncError;
    this.syncFailedAt = syncFailedAt;
    this.volunteerId = volunteerId;
    this.assignedStationAt = assignedStationAt;
    this.finalStationId = finalStationId;
    this.assignedAt = assignedAt;
    this.sparedAt = sparedAt;
    this.distributedAt = distributedAt;
    this.distributedOtherAt = distributedOtherAt;
    this.collectedAt = collectedAt;
    this.collectedSubToBankAt = collectedSubToBankAt;
    this.collectedEmptyAt = collectedEmptyAt;
    this.collectedOtherAt = collectedOtherAt;
    this.lostAt = lostAt;
    this.latedAt = latedAt;
    // Generate UUIDv7 for unique identification
    this.id = uuidv7();
  }

  /**
   * Set status and update the appropriate timestamp field
   * @param newStatus - The new status string (e.g., 'assigned', 'distributed', 'collected', etc.)
   * @param timestamp - Optional timestamp (ISO string). If not provided, uses current time
   */
  setStatus(newStatus: FlagbagServerStatus | string, timestamp?: string): void {
    const now = timestamp || new Date().toISOString();
    this.status = newStatus;

    // Clear all timestamp fields first (optional, but ensures clean state)
    // Then set the appropriate one based on status

    // Map status to timestamp field based on backend FLAGBAG_STATUSES
    const statusToTimestampMap: Record<string, keyof OfflineData> = {
      'assigned': 'assignedAt',
      'distributed': 'distributedAt',
      'distributedOther': 'distributedOtherAt',
      'distributed_other': 'distributedOtherAt',
      'collected': 'collectedAt',
      'collectedSubToBank': 'collectedSubToBankAt',
      'collected_sub_to_bank': 'collectedSubToBankAt',
      'collected_bank': 'collectedSubToBankAt', // Alternative format
      'collectedEmpty': 'collectedEmptyAt',
      'collected_empty': 'collectedEmptyAt', // Alternative format
      'collectedOther': 'collectedOtherAt',
      'collected_other': 'collectedOtherAt', // Alternative format
      'lost': 'lostAt',
      'late': 'latedAt',
      'lated': 'latedAt',
    };

    const timestampField = statusToTimestampMap[newStatus];
    if (timestampField) {
      // Set the appropriate timestamp field
      (this as any)[timestampField] = now;
    }

    // Also update the main updatedAt
    this.updatedAt = Math.floor(new Date(now).getTime() / 1000).toString();
  }

  get value() {
    return {
      id: this.id,
      stationID: this.stationID,
      flagBagID: this.flagBagID,
      flagbagNum: this.flagbagNum,
      updateType: this.updateType,
      dataType: this.dataType,
      status: this.status,
      isFreeUpdate: this.isFreeUpdate,
      name: this.name,
      phone: this.phone,
      updatedAt: this.updatedAt,
      volunteerId: this.volunteerId,
      userName: this.userName,
      syncError: this.syncError,
      syncFailedAt: this.syncFailedAt,
      assignedStationAt: this.assignedStationAt,
      finalStationId: this.finalStationId,
      assignedAt: this.assignedAt,
      sparedAt: this.sparedAt,
      distributedAt: this.distributedAt,
      distributedOtherAt: this.distributedOtherAt,
      collectedAt: this.collectedAt,
      collectedSubToBankAt: this.collectedSubToBankAt,
      collectedEmptyAt: this.collectedEmptyAt,
      collectedOtherAt: this.collectedOtherAt,
      lostAt: this.lostAt,
      latedAt: this.latedAt,
    }
  }

}

export const StatusLabel = {
  [FlagbagServerStatus.NOT_ASSIGN]: "未分配",
  [FlagbagServerStatus.DISTRIBUTED]: "未收",
  [FlagbagServerStatus.DISTRIBUTED_OTHER]: "未收（其他）",
  [FlagbagServerStatus.COLLECTED]: "實收",
  [FlagbagServerStatus.COLLECTED_EMPTY]: "吉袋",
  [FlagbagServerStatus.COLLECTED_OTHER]: "已收（其他）",
  [FlagbagServerStatus.BANK]: "入銀行",
  [FlagbagServerStatus.ASSIGNED]: "已分配/未出",
  [FlagbagServerStatus.LATE]: "遲交",
  [FlagbagServerStatus.LOSS]: "遺失",
};

export const FilterStatusLabel = {
  [FlagbagFilterStatus.ALL]: "旗袋總數",
  [FlagbagFilterStatus.NOT_ASSIGN]: StatusLabel[FlagbagServerStatus.NOT_ASSIGN],
  [FlagbagFilterStatus.ASSIGNED]: StatusLabel[FlagbagServerStatus.ASSIGNED],
  [FlagbagFilterStatus.DISTRIBUTED]: StatusLabel[FlagbagServerStatus.DISTRIBUTED],
  [FlagbagFilterStatus.DISTRIBUTED_OTHER]: StatusLabel[FlagbagServerStatus.DISTRIBUTED_OTHER],
  [FlagbagFilterStatus.COLLECTED]: StatusLabel[FlagbagServerStatus.COLLECTED],
  [FlagbagFilterStatus.BANK]: StatusLabel[FlagbagServerStatus.BANK],
  [FlagbagFilterStatus.COLLECTED_EMPTY]: StatusLabel[FlagbagServerStatus.COLLECTED_EMPTY],
  [FlagbagFilterStatus.COLLECTED_OTHER]: StatusLabel[FlagbagServerStatus.COLLECTED_OTHER],
  [FlagbagFilterStatus.RECEIVED_TOTAL]: "已收",
  [FlagbagFilterStatus.NOT_RECEIVED_TOTAL]: "未收",
  [FlagbagFilterStatus.EXTRA]: "多收",
  [FlagbagFilterStatus.LESS]: "少收",
  [FlagbagFilterStatus.RESERVED]: "後備",
  [FlagbagFilterStatus.LATE]: StatusLabel[FlagbagServerStatus.LATE],
  [FlagbagFilterStatus.LOSS]: StatusLabel[FlagbagServerStatus.LOSS],
};

export const StatusValue = {
  [FlagbagServerStatus.COLLECTED]: "COLLECTED",
  [FlagbagServerStatus.BANK]: "BANK",
  [FlagbagServerStatus.COLLECTED_EMPTY]: "COLLECTED_EMPTY",
  [FlagbagServerStatus.COLLECTED_OTHER]: "COLLECTED_OTHER",
  [FlagbagServerStatus.DISTRIBUTED]: "NOT_RECEIVED",
  [FlagbagServerStatus.LATE]: "LATE",
  [FlagbagServerStatus.LOSS]: "LOSS",
  [FlagbagServerStatus.DISTRIBUTED_OTHER]: "DISTRIBUTED_OTHER",
  [FlagbagServerStatus.ASSIGNED]: "ASSIGNED",
};

export interface FlagBagStatusItem {
  code: string;
  labelSc: string;
  labelTc: string;
  labelEn: string;
  description: string;
  timestampField: string | null;
}

export interface FlagBagStatusListResponse extends IResponse {
  data: FlagBagStatusItem[];
}
